import { Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import { FULL_DAYS, MATRIX_MODES } from "../../../consts/strings";
import { palette } from "../../../theme/palette";

import {
  hasForceUnlockMatrixRoleSelector,
  hasMapAdminRoleSelector,
} from "../../../reducers/ownUser/selectors";
import {
  anyDriverDayLockedSelector,
  anyRouteDayLockedSelector,
  getSelectedPlanDay,
  isDailyPlanSelector,
  isEditOneModeSelector,
  isWaveEditModeSelector,
  matrixProfileLockSelector,
  editRoutesSelector,
  editCollectionAreasSelector,
  matrixModeSelector,
} from "../../../reducers/matrixProfile/selectors";
import {
  setUnlockDepotModalOpen,
  setUnlockPlanModalOpen,
  setUnlockDriversDayModalOpen,
} from "../../../reducers/ownApp";
import {
  artificialPostcodeInEditSelector,
  depotLockSelector,
  mapEditModeSelector,
} from "../../../reducers/depot/selectors";
import { getSelectedDepot } from "../../../reducers/ownApp/selectors";
import {
  fetchLookUpTables,
  saveLookUpTables,
  unlockDepot,
} from "../../../reducers/depot/thunkActions";

import { View } from "../../View";
import { ActionButton } from "../Common/ActionButton";
import { CancelSaveActionButtons } from "../Common/CancelSaveActionButtons";
import { UnlockIcon } from "../../Icons";
import UnlockPlanModal from "../../Modals/UnlockPlanModal/UnlockPlanModal";
import UnlockDepotModal from "../../Modals/UnlockDepotModal/UnlockDepotModal";
import UnlockDriversDayModal from "../../Modals/UnlockDriversDayModal/UnlockDriversDayModal";

const useStyles = makeStyles(({ typography }) => ({
  lockedByText: {
    color: palette.typography.white.solid,
    display: "inline",
    fontSize: "12px",
    marginRight: "10px",
  },
  lockedDatetime: {
    color: palette.typography.white.inactive,
    display: "inline",
    fontSize: "12px",
    marginRight: "10px",
  },
  unlockIcon: {
    color: palette.typography.black.solid,
    width: "10px",
    height: "12px",
    marginRight: "3px",
  },
}));

const LockComponent = ({ onCancelClick, onSaveClick }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const selectedDepot = useSelector(getSelectedDepot);
  const dayOfWeek = useSelector(getSelectedPlanDay);
  const isDailyPlan = useSelector(isDailyPlanSelector);
  const mapEditMode = useSelector(mapEditModeSelector);
  const artificialPostcodeEdit = useSelector(artificialPostcodeInEditSelector);
  const isEditOneMode = useSelector(isEditOneModeSelector);
  const isWaveEditMode = useSelector(isWaveEditModeSelector);
  const depotLock = useSelector(depotLockSelector);
  const matrixProfileLock = useSelector(matrixProfileLockSelector);
  const anyDriverDayLocked = useSelector(anyDriverDayLockedSelector);
  const anyRouteDayLocked = useSelector(anyRouteDayLockedSelector);
  const hasForceUnlockMatrixRole = useSelector(
    hasForceUnlockMatrixRoleSelector
  );
  const hasMapAdminRole = useSelector(hasMapAdminRoleSelector);
  const editRoutes = useSelector(editRoutesSelector);
  const editCollectionAreas = useSelector(editCollectionAreasSelector);
  const matrixMode = useSelector(matrixModeSelector);

  const shouldDisableSave = () => {
    if (MATRIX_MODES.COLLECTION_TIMES_EDIT === matrixMode)
      return !editCollectionAreas;
    else
      return (
        !editRoutes ||
        isEditOneMode ||
        isWaveEditMode ||
        [MATRIX_MODES.NONE_SELECTED, MATRIX_MODES.VIEW].includes(matrixMode)
      );
  };

  const showForceUnlockMatrix =
    !depotLock.lockedByUserCode &&
    hasForceUnlockMatrixRole &&
    ((!!matrixProfileLock.lockedByUserCode && !matrixProfileLock.lockedByYou) ||
      matrixProfileLock.lockedInDifferentBrowser ||
      anyDriverDayLocked ||
      anyRouteDayLocked);

  const renderActionButtons = () => {
    if (mapEditMode)
      return (
        <CancelSaveActionButtons
          onCancelClick={async () => {
            await dispatch(unlockDepot());
            await dispatch(fetchLookUpTables(selectedDepot));
          }}
          onSaveClick={async () => {
            await dispatch(saveLookUpTables(selectedDepot));
            await dispatch(unlockDepot());
          }}
          saveDisabled={!!artificialPostcodeEdit}
        />
      );
    else if (
      !depotLock.lockedByUserCode &&
      matrixProfileLock.lockedByYou &&
      !matrixProfileLock.lockedInDifferentBrowser
    ) {
      return (
        <CancelSaveActionButtons
          onCancelClick={onCancelClick}
          onSaveClick={onSaveClick}
          saveDisabled={shouldDisableSave()}
        />
      );
    } else if (showForceUnlockMatrix) {
      return (
        <ActionButton
          variant={"yellow"}
          onClick={() => {
            dispatch(setUnlockPlanModalOpen(true));
          }}
        >
          <UnlockIcon className={classes.unlockIcon} />
          {isDailyPlan
            ? `Unlock ${FULL_DAYS[dayOfWeek]} Matrix`
            : "Unlock Plan"}
        </ActionButton>
      );
    } else if (
      ((depotLock.lockedByUserCode && !depotLock.lockedByYou) ||
        depotLock.lockedInDifferentBrowser) &&
      hasMapAdminRole
    ) {
      return (
        <ActionButton
          variant={"yellow"}
          onClick={() => {
            dispatch(setUnlockDepotModalOpen(true));
          }}
        >
          <UnlockIcon className={classes.unlockIcon} />
          Unlock Depot
        </ActionButton>
      );
    }
  };

  return (
    <View>
      <View>
        {!depotLock.lockedByUserCode && matrixProfileLock.lockedByUserCode && (
          <Typography className={classes.lockedByText}>
            {matrixProfileLock.lockedByText}
          </Typography>
        )}
        {depotLock.lockedByUserCode && (
          <Typography className={classes.lockedByText}>
            {depotLock.lockedByText}
          </Typography>
        )}

        {!depotLock.lockedByUserCode && matrixProfileLock?.lockedDatetime && (
          <Typography className={classes.lockedDatetime}>
            {moment(matrixProfileLock.lockedDatetime).format("HH:mm DD/MM/YY")}
          </Typography>
        )}
        {depotLock.lockedDatetime && (
          <Typography className={classes.lockedDatetime}>
            {moment(depotLock.lockedDatetime).format("HH:mm DD/MM/YY")}
          </Typography>
        )}

        {renderActionButtons()}
        {isDailyPlan &&
          !matrixProfileLock.lockedByYou &&
          !showForceUnlockMatrix &&
          anyDriverDayLocked && (
            <ActionButton
              variant={"yellow"}
              onClick={() => {
                dispatch(setUnlockDriversDayModalOpen(true));
              }}
            >
              <UnlockIcon className={classes.unlockIcon} />
              Unlock {FULL_DAYS[dayOfWeek]} Drivers
            </ActionButton>
          )}
      </View>

      <UnlockPlanModal />
      <UnlockDepotModal />
      <UnlockDriversDayModal />
    </View>
  );
};

export default LockComponent;
